import { compact, compose, filter, get, groupBy, keys } from 'lodash/fp';
import { createSelector } from 'reselect';
import { OrderItem } from '@mews-commander/types';
import { OrderItemType } from '@mews-commander/data-access';
import { getStringProp } from '@mews/fp';
import { getSelectedReservationsOrderItems, getServiceSelectedReservationsOrderItems } from './reservationsData';
import { additionalItemTypes, calculateOrderItemsCount } from './utils';
import { AdditionalOrderItemType } from './types';

const getReservationsProductOrders = createSelector(
    getSelectedReservationsOrderItems,
    filter<OrderItem>({ type: OrderItemType.ProductOrder, isCanceled: false })
);

const getOrderItemsGroupedByProduct = createSelector(getReservationsProductOrders, groupBy<OrderItem>('productId'));

const getServiceReservationsProductOrders = createSelector(
    getServiceSelectedReservationsOrderItems,
    filter<OrderItem>({ type: OrderItemType.ProductOrder, isCanceled: false })
);

const getServiceOrderItemsGroupedByProduct = createSelector(
    getServiceReservationsProductOrders,
    groupBy<OrderItem>('productId')
);

export const getServiceUniqueProductIds = createSelector(getServiceOrderItemsGroupedByProduct, compose(compact, keys));

const getOrderItemsByProduct = createSelector(getStringProp('productId'), getOrderItemsGroupedByProduct, get);

export const getProductCount = createSelector(getOrderItemsByProduct, calculateOrderItemsCount);

const getServiceAdditionalOrderItems = createSelector(
    getServiceSelectedReservationsOrderItems,
    compose(
        (orderItemsMap) =>
            additionalItemTypes.reduce(
                (acc, key) => {
                    const currentKeyItems = orderItemsMap[key];
                    if (currentKeyItems) {
                        acc[key] = currentKeyItems;
                    }
                    return acc;
                },
                {} as Record<AdditionalOrderItemType, OrderItem[]>
            ),
        groupBy<OrderItem>(({ type }) => type),
        filter<OrderItem>({ isCanceled: false })
    )
);

export const getServiceAdditionalItemsKeys = compose(
    (additionalOrderItems) => keys(additionalOrderItems) as AdditionalOrderItemType[],
    getServiceAdditionalOrderItems
);

const getServiceOrderItemsByType = createSelector(getStringProp('type'), getServiceAdditionalOrderItems, get);

export const getServiceAdditionalItemsCount = createSelector(getServiceOrderItemsByType, calculateOrderItemsCount);
